import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import KeyboardConnectMidi from 'assets/images/KeyboardConnectMidi.png';

// Full-screen blocking modal shown when the app is offline.
// Does not rely on external CSS; fully styled here.
const OfflineModal: React.FC = () => {
  const [online, setOnline] = useState<boolean>(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  if (online) return null;

  return (
    <>
      {/* Backdrop overlay */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.6)',
          zIndex: 3010,
        }}
      />

      {/* Foreground modal content */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 3001,
          pointerEvents: 'auto', // block and own interactions
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: 'min(900px, 90vw)',
            minHeight: '40vh',
            borderRadius: '8px',
            background: 'linear-gradient(229.67deg, #1D2F44 -6.17%, #071423 89.04%)',
            overflow: 'hidden',
            px: { xs: 3, md: 6 },
            py: { xs: 6, md: 8 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {/* Rotating broken/keyboard image for visual consistency with 404 */}
          <Box sx={{ position: 'absolute', animation: 'error-page-rotation 6s infinite linear' }}>
            <img
              style={{ maxWidth: '1000px', width: '75vw', opacity: 0.3 }}
              src={KeyboardConnectMidi}
              alt="offline-rotation"
            />
          </Box>

          <Box sx={{ position: 'relative', textAlign: 'center' }}>
            <Box
              sx={{
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: { xs: '28px', md: '34px' },
                letterSpacing: '0.01em',
                color: '#CED0D4',
                mb: 2,
              }}
            >
              You are offline
            </Box>
            <Box
              sx={{
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: { xs: '16px', md: '20px' },
                lineHeight: '30px',
                letterSpacing: '0.01em',
                color: '#CED0D4',
                maxWidth: '700px',
                mx: 'auto',
              }}
            >
              Please check your internet connection. This screen will close automatically once you
              are back online.
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default OfflineModal;
